import React, { CSSProperties, ReactElement, ReactNode } from 'react'
import styled from 'styled-components'
import Select from 'react-select'
import colors from '../lib/colors'
import theme from '../lib/theme'
import { GenericObject } from '../types/typeAliases'
import { capitalize, toSentenceCase } from '../helpers/dataTransformer'
import FieldStyles from '../components/FieldStyles'

const StyledSelect = styled(Select)`
  .select__control {
    font-family: ${theme.fontFamily};
    color: ${colors.black};
    transition: border-color 0.3s;
    :hover {
      border-color: ease-in-out ${colors.darkerGray};
    }
    :focus {
      transition: none;
      outline: none;
      border-color: ${colors.lightBlue};
    }
    box-shadow: none;
    min-width: 180px;
    cursor: text;
    min-height: ${theme.fieldHeight};
  }
  &.input-unsearchable .select__control {
    cursor: pointer;
  }
  .select__indicators {
    cursor: pointer;
  }
  .select__indicator {
    padding: 0 2px;
  }
  .select__control--is-focused,
  .select__control--is-focused ~ .select__control,
  .select__control--is-focused ~ .select__control:hover {
    border-color: ${colors.lightBlue};
    &:hover {
      border-color: ${colors.lightBlue};
    }
  }
  .select__menu {
    font-family: ${theme.fontFamily};
    color: ${colors.black};
    min-width: 180px;
    position: absolute;
  }
  .select__menu-list {
    ${theme.scrollbar}
  }
  &.input-long .select__menu {
    min-width: 300px;
  }
  .select__option--is-focused {
    background-color: inherit;
  }
  .select__option--is-selected {
    background-color: ${colors.lightGray};
    color: ${colors.black};
  }
  .select__option--is-focused,
  .select__option--is-selected {
    &:hover {
      background-color: ${colors.lightestBlue};
    }
  }
  .select__multi-value {
    border-radius: 4px;
    background-color: ${colors.lightGray};
  }
  .select__multi-value__remove {
    color: ${colors.darkerGray};
    cursor: pointer;
    margin-top: 1px;
  }
  .select__multi-value__remove:hover {
    color: ${colors.darkerGray};
    background-color: transparent;
  }
  &.error .select__control {
    border-color: ${colors.red};
    color: ${colors.red};
    background-color: ${colors.lightestRed};
  }
  .select__control--is-disabled {
    background-color: ${colors.white};
    border-color: ${colors.lightGray};
  }
  .select__value-container {
    padding: 0 4px;
  }
  .select__single-value {
    color: ${colors.black};
  }
  .select__option:first-child {
    font-weight: ${(props): string => props.theme.clearLabel};
  }
  &.input-small .select__control {
    min-height: 22px;
    min-width: 100px;
  }
  &.input-small .select__menu {
    min-width: 100px;
    font-size: 0.8rem;
  }
  &.input-small .select__single-value {
    font-size: 0.8rem;
  }
  &.input-small .select__input-container {
    padding: 0;
    margin: 0;
  }
`

// T is the type of the `value` of ISelectOption
// K is type of the selected option - default ISelectOption<T>, or ISelectOption<T>[] if `isMulti`
export type SelectFieldProps<T, K = ISelectOption<T>> = {
  isError?: boolean
  isMulti?: boolean
  options: ISelectOption<T>[]
  isClearable?: boolean
  disabled?: boolean
  value?: K
  isLong?: boolean
  label?: string
  name?: string
  placeholder?: string
  onChange?: (option: K | null) => void
  className?: string
  style?: CSSProperties
  isSearchable?: boolean
  clearLabel?: string | false
  isSmall?: true
}

export interface ISelectOption<T = string> {
  value: T
  label: ReactNode
}

export const enumToOptions = <T extends {}>(
  enumObject: GenericObject<T>,
): ISelectOption<T>[] => {
  const options = Object.keys(enumObject).map((label) => ({
    value: enumObject[label],
    label: toSentenceCase(label),
  }))

  return options
}
export const enumKeyByValue = (
  enumObject: GenericObject<string>,
  enumValue: string,
): string | null => {
  const keys = Object.keys(enumObject).filter(
    (key) => enumObject[key] === enumValue,
  )
  if (keys.length > 0) {
    return toSentenceCase(keys[0])
  }
  return null
}

export const stringsToOptions = (input: string[]): ISelectOption<string>[] => {
  const options = input.map((option) => ({
    value: option,
    label: option,
  }))
  return options
}
export const stringToOption = (
  input: string,
): ISelectOption<string> | undefined => {
  if (input) {
    return {
      value: input,
      label: capitalize(input),
    }
  }
  // if the value is undefined, the placeholder will show
  return undefined
}

function SelectField<T, K = ISelectOption<T>>(
  props: SelectFieldProps<T, K>,
): ReactElement {
  const {
    name,
    placeholder,
    isError,
    isMulti,
    isClearable,
    options,
    onChange,
    value,
    disabled,
    isLong,
    label,
    className,
    style,
    isSearchable = true,
    clearLabel = 'Show all',
    isSmall,
  } = props

  const optionsHandler = (
    // optionsHandler doesn't need to know about the value types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    allOptions: ISelectOption<any>[],
  ): ISelectOption<T>[] => {
    const clearOption = { value: '', label: clearLabel }
    if (
      clearLabel
      && isClearable
      && allOptions[0]?.label !== clearOption?.label
    ) {
      allOptions.unshift(clearOption)
    }
    return allOptions
  }

  const changeHandler = (option: unknown): void => {
    if (onChange) {
      if (option) {
        // onChange comes from react-select as `unknown`, but we know it is `K` (usually
        // `ISelectOption<T>`, or ISelectOption<T>[] if `isMulti`)
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        onChange(option as K)
      } else {
        // if is cleared
        onChange(null)
      }
    }
  }

  const errorClasses = isError ? 'error' : ''
  const lengthClasses = isLong ? 'input-long' : ''
  const searchClasses = !isSearchable ? 'input-unsearchable' : ''
  const smallClasses = isSmall ? 'input-small' : ''
  const classes = `${errorClasses} ${lengthClasses} ${searchClasses} ${smallClasses}`

  return (
    <FieldStyles label={label} style={style}>
      <StyledSelect
        name={name}
        placeholder={placeholder}
        isDisabled={!options[0] || disabled}
        classNamePrefix="select"
        className={`${className || ''} ${classes}`}
        options={optionsHandler(options)}
        onChange={changeHandler}
        isSearchable={isSearchable}
        isMulti={isMulti}
        isClearable={isClearable}
        theme={{
          clearLabel: isClearable && clearLabel ? 'bold' : 'normal',
        }}
        value={value || ''}
      />
    </FieldStyles>
  )
}

export default SelectField
